// /pricing — the standalone, linkable pricing page (per-page research 2026-08-02, top-leverage
// item #1: a procurement committee needs a URL it can forward, and the competitive teardown found
// only quote-gated or self-contradicting pricing across the Indian school-ERP market).
// Fixed self-serve prices render only from the reconciled billing catalogue; per-student bands are
// a separately labelled proposal model. The live entitlement matrix remains authoritative for
// access, while release readiness is displayed separately. Terms use registry-reviewed wording.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { cn } from "../lib/utils";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useReducedMotion } from "framer-motion";
import {
  ArrowRight, Check, Landmark, ShieldCheck, CalendarCheck, DatabaseBackup, HelpCircle,
} from "lucide-react";
import { getInitialMarketingContent, getMarketingContent, subscribeMarketingContent } from "../lib/marketingContent";
import { MarketingTopBar, SiteFooter, ContactSalesModal } from "../components/marketing/shared";
import { MarketingMotion } from "../components/marketing/MarketingMotion";
import { ScrollProgressBar } from "../components/marketing/scrollMotion";
import MarketingSeo from "../components/marketing/MarketingSeo";
import ButtonLink from "../components/marketing/ButtonLink";
import { ORGANIZATION_ID } from "../components/marketing/organizationSchema";
import { Button, ErrorState, Skeleton } from "../components/ui/primitives";
import { fromConfig, toStudents } from "../lib/pricingTiers";
import { catalogueCounts, fillCopyTokens } from "../lib/marketingCounts";
import {
  CampaignStrip,
  PlanCard,
  buildTierCards,
  campaignFrom,
  mapMatrixPlans,
  pricingTaxDisplay,
} from "./landingparts/pricingShared";
import KnowYourPrice from "./landingparts/KnowYourPrice";
import PricingOtherCharges from "./landingparts/PricingOtherCharges";
import PricingFeatureCatalogue, {
  buildPricingCatalogue, resolveCoreGroups, resolveCoreModuleLabels, resolveFeatureGroups,
} from "./landingparts/PricingFeatureCatalogue";
import { planModulesForCard } from "./landingparts/PlanModules";
import PricingVoiceAi from "./landingparts/PricingVoiceAi";
import { init as initAnalytics, track } from "../lib/analytics";
import { CTA_PRIMARY_LABEL } from "../components/marketing/navigation";

// Every static copy block on this page is platform-editable (CMS `pricing_page` section,
// Platform Admin → Landing CMS). PRICING_PAGE_FALLBACK byte-mirrors core/marketing.py
// DEFAULTS["pricing_page"] (pinned by test_marketing_copy_mirror_contract.py) — change BOTH
// together. The literal token "{tax}" is replaced at render time by the live pricing-engine
// tax-display string (pricingTaxDisplay), so copy can position it but never rewrite it.
export const PRICING_PAGE_FALLBACK = Object.freeze({
  heading: "One price for your whole school.",
  subhead: "₹99 to ₹249 per student per year, by plan. Type your student count and see your exact price — before anyone calls you. {tax}.",
  students_label: "How many students do you have?",
  students_help: "Active students on your roll. An estimate is fine — we count again at renewal.",
  reassurance: Object.freeze([
    "Free forever under 100 students",
    "No credit card to start",
    "{tax}",
  ]),
  minimums_note: "Minimums: Essentials ₹9,999 · Growth ₹49,999 · Pro ₹99,999 a year. A school below the minimum pays the minimum and nothing else.",
  other_charges_heading: "What else could you pay?",
  other_charges: Object.freeze([
    Object.freeze({ id: "gateway", title: "Payment gateway charges", body: "Set by your own Razorpay or Cashfree account. UPI is free. Fees settle to your school's bank account and we add nothing to a payment." }),
    Object.freeze({ id: "messaging", title: "WhatsApp and SMS", body: "Nothing extra on your school's own WhatsApp Business account. Prefer to use our number? ₹0.25 a message, billed as you send." }),
    Object.freeze({ id: "addons", title: "Optional add-ons", body: "HR & Payroll ₹59 per employee a month · Bus GPS ₹2,999 per vehicle a year · Go-live help from ₹14,999. Each one is quoted before you buy it." }),
  ]),
  catalogue_heading: "Compare plans, feature by feature",
  catalogue_subhead: "The {core_modules} modules every school gets, what each plan adds, and what is still rolling out — listed separately, so there are no surprises.",
  terms_heading: "The fine print, in large print.",
  close_heading: "Start free. Decide later.",
  close_subhead: "Create your school, import your students, and pick a plan when you are ready. Under 100 students, free is not a trial — it is the plan.",
  close_secondary_cta: "Book a guided demo",
});

// Copy tokens → live values. "{tax}" is the pricing engine's tax-display string and
// "{core_modules}"/"{total_modules}"/"{capabilities}" are the live catalogue counts; the token
// itself is never shown to a visitor. See lib/marketingCounts for why a count is never typed.
const withTax = (text, taxDisplay, counts) => fillCopyTokens(text, { tax: taxDisplay, counts });

// The subhead carries the two figures that decide whether a school reads on, and they were the
// same weight and colour as the words around them. This lifts the ₹ amounts out of the sentence
// WITHOUT retyping them: the copy stays CMS-owned and the prices stay served, so a platform edit
// to either still renders correctly and nothing here can state a price of its own.
function HighlightedPrices({ text }) {
  const parts = String(text || "").split(/(\u20b9[\d,]+(?:\.\d+)?)/g);
  return (
    <>
      {parts.map((part, i) => (
        /^\u20b9[\d,]/.test(part)
          ? (
            <strong key={i} className="font-heading font-extrabold text-accent-active tabular-nums">
              {part}
            </strong>
          )
          : <React.Fragment key={i}>{part}</React.Fragment>
      ))}
    </>
  );
}

// Registry-verified terms only — the answers a committee looks for and quote-gated
// competitors never publish. (P4 trial · C3 self-serve · S5 export/erasure · X1 BYOG.)
const TERMS = [
  [CalendarCheck, "The price on this page is the price on your invoice", "Your student count times your plan's rate, or the plan minimum — whichever is higher. GST is added on top. Nothing else appears on the bill."],
  [Landmark, "We take no cut of your fees", "Fees settle to your school's own bank account through your own Razorpay or Cashfree account. Their charges are theirs; we deduct nothing."],
  [CalendarCheck, "The trial does not become a paid plan on its own", "The 90-day Pro trial ends when it ends. Paid service starts only after someone at your school picks a plan and completes checkout."],
  [DatabaseBackup, "Data access and exit are documented", "Use supported exports while access is active, and request access or erasure through the documented process. Retention, legal holds, backups and deletion timing follow the applicable agreement and India's phased DPDP framework."],
];

export default function PricingPage() {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  // Seed from the prerender snapshot's embedded payload SYNCHRONOUSLY: the first hydrated
  // render must match the snapshot exactly, or the priced grid repaints and shifts (the
  // Lighthouse desktop CLS failure). Falls back to null (then the shared fetch) off-snapshot.
  const [cms, setCms] = useState(getInitialMarketingContent);
  const [cmsError, setCmsError] = useState(false);
  const [salesOpen, setSalesOpen] = useState(false);

  const loadCms = useCallback(() => {
    setCmsError(false);
    // Shared session-memoized fetch (lib/marketingContent) — the SiteFooter's own CMS read
    // rides the same inflight promise, so the page issues ONE /public/marketing request. A
    // failure resolves null AND clears the memo, so this retry path refetches for real.
    getMarketingContent().then((data) => (data ? setCms(data) : setCmsError(true)));
  }, []);
  useEffect(() => { loadCms(); }, [loadCms]);
  // CMS-3: a page landing on a prerendered snapshot seeds `cms` from the deploy-time bootstrap
  // above and, before this, never checked again — a pricing/CMS publish was invisible here until
  // the next deploy. Subscribe to the shared module's background revalidation so a later publish
  // (checked right after the bootstrap loads, and on tab refocus) refreshes the priced grid in
  // place. A background check that never lands fresh data leaves `cmsError` untouched (it only
  // ever came from a FAILED loadCms()/retry, so a silent no-op revalidation can't invent one) —
  // but one that DOES deliver data means the content is live and correct, so it also clears
  // `cmsError`: a fresh payload arriving after an earlier failed manual retry must not leave
  // the priced grid (which reads
  // the priced grid's `commercialVerified` state) permanently stuck on stale error copy.
  useEffect(() => subscribeMarketingContent((data) => { setCms(data); setCmsError(false); }), []);
  useEffect(() => {
    initAnalytics();
    window.scrollTo(0, 0);
    track("pricing_view", { placement: "pricing_page" });
  }, []);

  const model = useMemo(() => fromConfig(cms?.pricing_model), [cms]);
  // The count lives in the URL so a head can send "our price" to a committee and they open the
  // same page, already priced. Nothing about the school is in it — a student count is not
  // personal data, and no name, email or school id ever enters the query string.
  const [params, setParams] = useSearchParams();
  const [students, setStudents] = useState(() => toStudents(params.get("students")));
  // Opens on ANNUAL. Annual is the headline price, the one the minimums are stated in and the one
  // the invoice charges; opening on monthly would show every visitor a surcharged figure they
  // never asked for, and opening on the LONGEST term (the consumer-subscription habit) anchors on
  // a prepay the buyer has not chosen.
  const [termKey, setTermKey] = useState("yearly");
  const onCalcChange = useCallback(({ students: n, termKey: k }) => {
    setStudents(n);
    setTermKey(k);
    setParams((p) => {
      const next = new URLSearchParams(p);
      if (n > 0) next.set("students", String(n)); else next.delete("students");
      return next;
    }, { replace: true });
  }, [setParams]);
  // The ONE size-of-product answer this page is allowed to state. Served from the live
  // entitlement registries, with a plan_matrix-derived fallback — never a literal.
  const counts = useMemo(() => catalogueCounts(cms), [cms]);
  const cards = useMemo(
    () => buildTierCards(model, cms?.pricing_integrity, cms?.pricing_page, termKey, students, counts),
    [model, cms, termKey, students, counts],
  );
  const matrixPlans = useMemo(
    () => mapMatrixPlans(cms?.plan_matrix?.plans, cards),
    [cms, cards],
  );
  // Computed once and handed to both the strip AND every card, so they can never disagree about
  // whether an offer is active (a client-side date re-check happens inside campaignFrom itself).
  const pricingCampaign = useMemo(() => campaignFrom(cms?.pricing_integrity), [cms]);
  // The SAME catalogue the comparison table below is built from — same resolvers, same platform
  // edit reactivity — so a card's expanded module list and the table beneath it can never name a
  // module differently or disagree about which plan grants it. Computed once here and handed to
  // every card, rather than each card resolving its own copy.
  const catalogue = useMemo(() => buildPricingCatalogue(
    cms?.plan_matrix,
    resolveFeatureGroups(cms?.pricing_page),
    resolveCoreGroups(cms?.pricing_page),
    resolveCoreModuleLabels(cms?.pricing_page),
  ), [cms]);
  // All five cards expand together — a school comparing "what does Growth have that Essentials
  // doesn't" needs the same group visible down every column at once, not one card alone growing
  // taller than its neighbours.
  const [modulesExpanded, setModulesExpanded] = useState(false);
  const toggleModules = useCallback(() => {
    setModulesExpanded((prev) => {
      const next = !prev;
      track(next ? "pricing_modules_expand" : "pricing_modules_collapse", { placement: "pricing_page" });
      return next;
    });
  }, []);
  const commercialStatus = cms?.pricing_integrity?.status || "unavailable";
  const taxDisplay = pricingTaxDisplay(cms?.pricing_integrity);
  // Platform-editable page copy: a published non-blank CMS value wins per field; anything
  // blank/missing falls back to the byte-mirrored seed above (same rule as _merge_section).
  const copy = useMemo(() => {
    const stored = cms?.pricing_page || {};
    const pick = (k) => (typeof stored[k] === "string" && stored[k].trim() ? stored[k] : PRICING_PAGE_FALLBACK[k]);
    return {
      heading: pick("heading"),
      subhead: pick("subhead"),
      students_label: pick("students_label"),
      students_help: pick("students_help"),
      minimums_note: pick("minimums_note"),
      other_charges_heading: pick("other_charges_heading"),
      other_charges: Array.isArray(stored.other_charges) && stored.other_charges.length
        ? stored.other_charges
        : PRICING_PAGE_FALLBACK.other_charges,
      catalogue_heading: pick("catalogue_heading"),
      catalogue_subhead: pick("catalogue_subhead"),
      terms_heading: pick("terms_heading"),
      close_heading: pick("close_heading"),
      close_subhead: pick("close_subhead"),
      close_secondary_cta: pick("close_secondary_cta"),
      reassurance: Array.isArray(stored.reassurance) && stored.reassurance.length
        ? stored.reassurance
        : PRICING_PAGE_FALLBACK.reassurance,
    };
  }, [cms]);
  // WP-04: the catalogue-integrity check stays, but its output is an ENGINEERING signal —
  // it goes to the console/monitoring, never to a public banner above the plan cards.
  useEffect(() => {
    if (cms && commercialStatus !== "aligned") {
      // eslint-disable-next-line no-console
      console.warn(`[pricing] catalogue integrity: status=${commercialStatus} — proposal configuration and self-serve billing catalogue disagree; resolve in Platform Admin.`);
    }
  }, [cms, commercialStatus]);
  // WP-21: Offer markup from the LIVE billing catalogue (INR) — emitted only when real prices
  // exist; never a hard-coded price and never invented ratings/reviews.
  const pricingSchema = useMemo(() => {
    // The per-student ANNUAL rate, from the served price book. `unitCode: "ANN"` + `referenceQuantity`
    // say what the figure IS — a rate per student per year — because an `Offer` with a bare `149`
    // would be read by an aggregator as the price of the product.
    const offers = (cms?.pricing_integrity?.tiers || [])
      .filter((t) => Number.isInteger(t?.rate_paise_per_student_year) && t.rate_paise_per_student_year > 0)
      .map((t) => ({
        "@type": "Offer",
        name: `Vidyapeeth360 ${String(t.key || "").charAt(0).toUpperCase()}${String(t.key || "").slice(1)} plan`,
        price: String(Math.round(t.rate_paise_per_student_year / 100)),
        priceCurrency: "INR",
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: String(Math.round(t.rate_paise_per_student_year / 100)),
          priceCurrency: "INR",
          unitCode: "ANN",
          referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitText: "student" },
        },
        url: "https://vidyapeeth360.com/pricing",
      }));
    if (!offers.length) return undefined;
    return {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Vidyapeeth360",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      offers,
      publisher: { "@id": ORGANIZATION_ID },
    };
  }, [cms]);

  return (
    <MarketingMotion>
    <div className="min-h-screen bg-canvas text-ink flex flex-col landing-page" data-testid="pricing-page">
      <MarketingSeo
        title="School ERP Pricing — ₹99–₹249 per student a year | Vidyapeeth360"
        description="₹99 to ₹249 per student per year. Type your student count and see your school's exact price, with every plan's features compared side by side."
        path="/pricing"
        image="/og/og-pricing.jpg"
        schema={pricingSchema}
      />
      <ScrollProgressBar />
      <MarketingTopBar onBookDemo={() => setSalesOpen(true)} />
      <main className="flex-1">
        {/* Hero + THE input. One line, one sentence, one field: everything below this section is
            priced by the number typed here, so it comes before the cards rather than beside them. */}
        <section className="mx-auto max-w-6xl px-5 pt-10 sm:pt-14 pb-6 text-center">
          <h1 className="font-heading font-bold text-3xl sm:text-[40px] leading-[1.12] text-ink tracking-tight max-w-2xl mx-auto">
            {copy.heading}
          </h1>
          <p className="mt-4 text-ink-2 text-[16.5px] sm:text-[18px] leading-relaxed max-w-2xl mx-auto">
            <HighlightedPrices text={withTax(copy.subhead, taxDisplay, counts)} />
          </p>
          <div className="mt-8 text-left">
            <KnowYourPrice model={model} copy={copy} onChange={onCalcChange}
                           initialStudents={students} />
          </div>
        </section>

        {/* Fixed plans */}
        <section className="mx-auto max-w-7xl px-5 pb-4" aria-label="Plans and prices">
          {cmsError && cms === null ? (
            <div className="max-w-md mx-auto py-10">
              <ErrorState title="Couldn't load live pricing" desc="Live prices didn’t load just now. Try again, or ask us for a written proposal with exact figures." onRetry={loadCms} retryLabel="Reload pricing" />
            </div>
          ) : cms === null ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="pricing-page-loading" aria-busy="true">
              {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-[520px] rounded-[28px]" />)}
            </div>
          ) : (
            /* WP-08 + WP-12: the cards are now the FIRST viewport of /pricing — they must paint
               at opacity 1, so no mount-play reveal here (a plain grid; PlanCard's hover motion
               is unaffected). */
            <>
            {/* Governed CAMPAIGN strip (launch/festival offer) — the ONE sanctioned promotional
                banner, in-flow beside the prices it modifies, dismissible, plain end date (no
                countdown). Server-resolved + client date-guard; renders null when inactive. */}
            <CampaignStrip campaign={pricingCampaign} />
            {/* Billing-term switch. NATIVE radios in a fieldset, visually hidden, with styled
                labels — the Notion pattern. A `role="switch"` cannot express three peer options,
                and a hand-rolled `role="radiogroup"` of buttons would need its own arrow-key
                handling; real radios give arrow navigation, a single tab stop and "2 of 3"
                position announcements for free. The savings sits INSIDE the label so it becomes
                part of that option's accessible name ("1 year, save 17%") rather than a detached
                badge a screen-reader user never hears next to the choice it belongs to. */}
            {/* The billing-term switch lives in KnowYourPrice above, beside the count it
                prices — two controls that change the same five figures must not sit in two
                different places on the page. A single polite status line still announces the
                change once, rather than five cards announcing it five times. */}
            <p className="sr-only" role="status" data-testid="pricing-term-status">
              {`${cards[0]?.termLabel || "1 year"} billing selected. Prices updated.`}
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 items-stretch pt-4" data-testid="pricing-page-grid">
              {cards.map((pl) => (
                <PlanCard key={pl.planId} pl={pl} navigate={navigate} reduceMotion={reduceMotion}
                  onContactSales={() => { track("demo_request", { placement: "pricing_enterprise" }); setSalesOpen(true); }}
                  modules={planModulesForCard(catalogue, matrixPlans, pl.planId)}
                  modulesExpanded={modulesExpanded} onToggleModules={toggleModules}
                  campaign={pricingCampaign} />
              ))}
            </div>
            </>
          )}
          {/* Reassurance row + commercial caveat — BELOW the cards (WP-12), one quiet line each. */}
          <ul className="mt-8 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-ink-2">
            {copy.reassurance.map((raw) => withTax(raw, taxDisplay, counts)).map((t) => (
              <li key={t} className="inline-flex items-center gap-1.5">
                <Check className="w-4 h-4 text-positive shrink-0" aria-hidden="true" /> {t}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-center text-[13.5px] text-ink-2 max-w-2xl mx-auto"
             data-testid="pricing-minimums">
            {copy.minimums_note}
          </p>
        </section>

        <PricingOtherCharges copy={copy} />

        {/* The flagship, on the page where the decision is made. Until this landed, /pricing did
            not contain the words voice, dictation, speak, transcribe or meeting notes ANYWHERE —
            the capability with its own homepage band, /modules page, product film and 33-clip
            audio library had two auto-generated table rows here and nothing else. Copy is the
            published `voice_ai` CMS block (no new claim); every figure is served. */}
        {cms !== null && <PricingVoiceAi cms={cms} />}

        {/* Full catalogue — core, sellable entitlements and release-controlled capability are kept distinct. */}
        {cms !== null && (
          <section className="mx-auto max-w-6xl px-5 py-12 sm:py-14" aria-label="Compare all plans, feature by feature">
            <div className="text-center mb-8">
              <h2 className="font-heading font-bold text-[30px] sm:text-[34px] text-ink tracking-tight">{copy.catalogue_heading}</h2>
              <p className="text-ink-2 mt-2.5 max-w-2xl mx-auto">{withTax(copy.catalogue_subhead, taxDisplay, counts)}</p>
            </div>
            <PricingFeatureCatalogue onRetry={loadCms} planMatrix={cms.plan_matrix} plans={matrixPlans} copy={cms.pricing_page} releaseVisible={cms.sections?.pricing_release !== false} trial={cms?.pricing_integrity?.trial} />
          </section>
        )}

        {/* The terms a committee actually asks about — registry-verified facts only. */}
        <section className="border-y border-line bg-surface" aria-labelledby="pricing-terms-h">
          <div className="mx-auto max-w-6xl px-5 py-12 sm:py-14">
            <h2 id="pricing-terms-h" className="font-heading font-bold text-2xl sm:text-[28px] text-ink tracking-tight max-w-2xl">
              {copy.terms_heading}
            </h2>
            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              {/* Copy is CMS-owned (cms.pricing_page.terms); TERMS is the byte-mirrored fallback.
                  Icons stay code-owned, matched by position (extra CMS terms reuse the shield). */}
              {(cms?.pricing_page?.terms?.length
                ? cms.pricing_page.terms.map((term, index) => [TERMS[index]?.[0] || ShieldCheck, term.title, term.body])
                : TERMS
              ).map(([Icon, t, d]) => (
                <div key={t} className="flex items-start gap-4 rounded-2xl border border-line bg-canvas p-5">
                  <span className="w-10 h-10 rounded-xl bg-primary/10 grid place-items-center shrink-0"><Icon className="w-5 h-5 text-primary" aria-hidden="true" /></span>
                  <div className="min-w-0">
                    <h3 className="font-heading font-semibold text-ink text-[15px] leading-snug">{t}</h3>
                    <p className="mt-1.5 text-[14px] text-ink-2 leading-relaxed">{d}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-ink-2 inline-flex items-center gap-1.5">
              <HelpCircle className="w-4 h-4 text-ink-subtle" aria-hidden="true" />
              More questions? <Link to="/#faq" className="font-semibold text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary">Read the FAQ</Link> or{" "}
              <Link to="/contact" className="font-semibold text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary">talk to a real person</Link>.
            </p>
          </div>
        </section>

        {/* Close */}
        {/* pb clears the fixed Aira launcher at narrow widths (audit: it covered the last card CTA) */}
        <section className="mx-auto max-w-6xl px-5 pt-14 sm:pt-16 pb-24 lg:pb-16 text-center">
          <h2 className="font-heading font-bold text-[26px] sm:text-[30px] text-ink tracking-tight max-w-xl mx-auto leading-tight">
            {copy.close_heading}
          </h2>
          <p className="text-ink-2 mt-3 max-w-lg mx-auto leading-relaxed">
            {withTax(copy.close_subhead, taxDisplay, counts)}
          </p>
          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <ButtonLink to="/register" size="lg" onClick={() => track("trial_cta_click", { placement: "pricing_page" })} data-testid="pricing-page-register">
              {CTA_PRIMARY_LABEL} <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </ButtonLink>
            <Button size="lg" variant="outline" onClick={() => { track("demo_request", { placement: "pricing_page" }); setSalesOpen(true); }} data-testid="pricing-page-demo">
              {copy.close_secondary_cta}
            </Button>
          </div>
        </section>
      </main>
      <SiteFooter />
      <ContactSalesModal open={salesOpen} onClose={() => setSalesOpen(false)} contextLabel="plans, charges and your rollout scope" placement="pricing_page" />
    </div>
    </MarketingMotion>
  );
}
